import React, { useCallback, useMemo, useState } from 'react';
import { useAdminLanguage } from '../AdminLanguageContext';

export default function useAdminDialog() {
  const { copy } = useAdminLanguage();
  const dialogCopy = copy.dialog;
  const [dialogState, setDialogState] = useState(null);

  const confirmAction = useCallback((options = {}) => new Promise(resolve => {
    setDialogState({
      type: 'confirm',
      title: options.title || dialogCopy.confirmTitle,
      message: options.message || '',
      confirmText: options.confirmText || dialogCopy.confirm,
      cancelText: options.cancelText || dialogCopy.cancel,
      variant: options.variant || 'primary',
      resolve,
    });
  }), [dialogCopy.cancel, dialogCopy.confirm, dialogCopy.confirmTitle]);

  const requestText = useCallback((options = {}) => new Promise(resolve => {
    setDialogState({
      type: 'prompt',
      title: options.title || dialogCopy.promptTitle,
      message: options.message || '',
      placeholder: options.placeholder || '',
      defaultValue: options.defaultValue || '',
      required: Boolean(options.required),
      confirmText: options.confirmText || dialogCopy.confirm,
      cancelText: options.cancelText || dialogCopy.cancel,
      variant: options.variant || 'primary',
      resolve,
    });
  }), [dialogCopy.cancel, dialogCopy.confirm, dialogCopy.promptTitle]);

  const closeWith = useCallback((value) => {
    if (dialogState?.resolve) dialogState.resolve(value);
    setDialogState(null);
  }, [dialogState]);

  const setPromptValue = useCallback((value) => {
    setDialogState(current => current ? { ...current, defaultValue: value } : current);
  }, []);

  const dialog = useMemo(() => {
    if (!dialogState) return null;

    const isPrompt = dialogState.type === 'prompt';
    const promptValue = dialogState.defaultValue || '';
    const canSubmit = !isPrompt || !dialogState.required || promptValue.trim();
    const confirmClass = dialogState.variant === 'danger' ? 'btn-danger' : 'btn-primary';

    return (
      <div className="modal-overlay admin-dialog-overlay" role="presentation">
        <div className="modal admin-dialog" role="dialog" aria-modal="true" aria-labelledby="admin-dialog-title">
          <h3 id="admin-dialog-title">{dialogState.title}</h3>
          {dialogState.message && <p className="admin-dialog-message">{dialogState.message}</p>}
          {isPrompt && (
            <textarea
              className="admin-dialog-input"
              value={promptValue}
              placeholder={dialogState.placeholder}
              onChange={e => setPromptValue(e.target.value)}
              autoFocus
            />
          )}
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => closeWith(isPrompt ? null : false)}>
              {dialogState.cancelText}
            </button>
            <button
              className={`btn ${confirmClass}`}
              disabled={!canSubmit}
              onClick={() => closeWith(isPrompt ? promptValue : true)}
            >
              {dialogState.confirmText}
            </button>
          </div>
        </div>
      </div>
    );
  }, [closeWith, dialogState, setPromptValue]);

  return { confirmAction, requestText, dialog };
}
